import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { licensesAPI } from "../../services/api";

export interface License {
	_id: string;
	name: string;
	uid: string;
	clientUid?: string;
	accountId: {
		_id: string;
		name: string;
		contactEmail: string;
		isActive: boolean;
	};
	os: string;
	// Absent on licenses that predate license types, and null when the type was
	// removed outside the API - never assume it is there.
	licenseTypeId?: {
		_id: string;
		name: string;
		monthlyPrice: number;
	} | null;
	status: string;
	// The state the license reads as, resolved by the server. `status` is only
	// recomputed when a license is saved, so it lags behind expiry on its own.
	displayStatus: string;
	isActive: boolean;
	isUnlimited: boolean;
	// Absent on licenses created before the unlimited-licence option
	expirationDate?: string;
	deviceInfo: {
		deviceName?: string;
		deviceModel?: string;
		osVersion?: string;
		appVersion?: string;
		lastSeen?: string;
		ipAddress?: string;
	};
	tags: string[];
	// When the current station was bound; null once the license is unbound
	activationDate?: string | null;
	// Survives unbinding. Absent on licenses not activated since it was added.
	firstActivatedAt?: string;
	createdAt: string;
	licenseToken?: string; // Made optional for security - only fetched when needed
	isExpired?: boolean;
	isExpiringSoon?: boolean;
	remainingDays?: number;
	holdStartDate?: string;
	daysSinceHold?: number;
}

interface LicenseStats {
	total: number;
	active: number;
	inactive: number;
	expired: number;
	held: number;
}

interface LicensesState {
	licenses: License[];
	loading: boolean;
	error: string | null;
	// Survives the notification, which clears `error` as soon as it is shown.
	// Without it the table cannot tell "nothing matched" from "the request
	// failed", and reports a failure as an empty result.
	listError: string | null;
	total: number;
	page: number;
	limit: number;
	// Id of the most recent list request, so a slower earlier one cannot
	// overwrite it: a delete refreshes the current page while the page number is
	// being corrected, leaving two fetches in flight for different pages.
	latestFetchRequestId: string | null;
	stats: LicenseStats;
	// The same counts under the page's filters, null while unfiltered
	filteredStats: LicenseStats | null;
	latestFilteredStatsRequestId: string | null;
}

const initialState: LicensesState = {
	licenses: [],
	loading: false,
	error: null,
	listError: null,
	total: 0,
	page: 1,
	limit: 10,
	latestFetchRequestId: null,
	stats: {
		total: 0,
		active: 0,
		inactive: 0,
		expired: 0,
		held: 0,
	},
	filteredStats: null,
	latestFilteredStatsRequestId: null,
};

export interface LicenseStatsFilters {
	search?: string;
	status?: string;
	accountId?: string;
	licenseTypeId?: string;
}

// Async thunks
export const fetchFilteredLicenseStats = createAsyncThunk(
	"licenses/fetchFilteredLicenseStats",
	async (filters: LicenseStatsFilters, { rejectWithValue }) => {
		try {
			const response = await licensesAPI.getStats(filters);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch filtered license stats",
				);
			}
			return rejectWithValue("Failed to fetch filtered license stats");
		}
	},
);

export const fetchLicenseStats = createAsyncThunk(
	"licenses/fetchLicenseStats",
	async (_params: void, { rejectWithValue }) => {
		try {
			const response = await licensesAPI.getStats();
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch license stats",
				);
			}
			return rejectWithValue("Failed to fetch license stats");
		}
	},
);

export const fetchLicenses = createAsyncThunk(
	"licenses/fetchLicenses",
	async (
		params: {
			page?: number;
			limit?: number;
			search?: string;
			accountId?: string;
			licenseTypeId?: string;
			os?: string;
			status?: string;
			isActive?: boolean;
			sortBy?: string;
			sortOrder?: string;
		} = {},
		{ rejectWithValue },
	) => {
		try {
			const response = await licensesAPI.getAll(params);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					// Return a pending state or rethrow to let the interceptor handle it
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch licenses",
				);
			}
			return rejectWithValue("Failed to fetch licenses");
		}
	},
);

export const createLicense = createAsyncThunk(
	"licenses/createLicense",
	async (
		licenseData: {
			name: string;
			accountId: string;
			os: string;
			licenseTypeId?: string;
			isUnlimited?: boolean;
			expirationDate?: string;
			customDuration?: number;
			tags?: string[];
			status?: string;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await licensesAPI.create(licenseData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to create license",
				);
			}
			return rejectWithValue("Failed to create license");
		}
	},
);

export const updateLicense = createAsyncThunk(
	"licenses/updateLicense",
	async (
		{
			id,
			...licenseData
		}: {
			id: string;
			name?: string;
			accountId?: string;
			isActive?: boolean;
			licenseTypeId?: string;
			isUnlimited?: boolean;
			expirationDate?: string;
			tags?: string[];
			status?: string;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await licensesAPI.update(id, licenseData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to update license",
				);
			}
			return rejectWithValue("Failed to update license");
		}
	},
);

export const deleteLicense = createAsyncThunk(
	"licenses/deleteLicense",
	async (id: string, { rejectWithValue }) => {
		try {
			await licensesAPI.delete(id);
			return id;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to delete license",
				);
			}
			return rejectWithValue("Failed to delete license");
		}
	},
);

export const holdLicense = createAsyncThunk(
	"licenses/holdLicense",
	async (
		{ id, action }: { id: string; action: "hold" | "release" },
		{ rejectWithValue },
	) => {
		try {
			const response = await licensesAPI.hold(id, action);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || `Failed to ${action} license`,
				);
			}
			return rejectWithValue(`Failed to ${action} license`);
		}
	},
);

export const bulkRevokeLicenses = createAsyncThunk(
	"licenses/bulkRevoke",
	async (licenseIds: string[], { rejectWithValue }) => {
		try {
			const response = await licensesAPI.bulkRevoke(licenseIds);
			return response.data.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to revoke licenses",
				);
			}
			return rejectWithValue("Failed to revoke licenses");
		}
	},
);

export const bulkHoldLicenses = createAsyncThunk(
	"licenses/bulkHold",
	async (licenseIds: string[], { rejectWithValue }) => {
		try {
			const response = await licensesAPI.bulkHold(licenseIds);
			return response.data.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to hold licenses",
				);
			}
			return rejectWithValue("Failed to hold licenses");
		}
	},
);

export const bulkReleaseLicenses = createAsyncThunk(
	"licenses/bulkRelease",
	async (licenseIds: string[], { rejectWithValue }) => {
		try {
			const response = await licensesAPI.bulkRelease(licenseIds);
			return response.data.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to release licenses",
				);
			}
			return rejectWithValue("Failed to release licenses");
		}
	},
);

export const bulkRenewLicenses = createAsyncThunk(
	"licenses/bulkRenew",
	async (
		{
			licenseIds,
			expirationDate,
		}: { licenseIds: string[]; expirationDate: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await licensesAPI.bulkRenew(licenseIds, expirationDate);
			return response.data.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to renew licenses",
				);
			}
			return rejectWithValue("Failed to renew licenses");
		}
	},
);

export const bulkDeleteLicenses = createAsyncThunk(
	"licenses/bulkDelete",
	async (licenseIds: string[], { rejectWithValue }) => {
		try {
			await licensesAPI.bulkDelete(licenseIds);
			return { licenseIds };
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to delete licenses",
				);
			}
			return rejectWithValue("Failed to delete licenses");
		}
	},
);

export const bulkSetLicenseType = createAsyncThunk(
	"licenses/bulkSetType",
	async (
		{ licenseIds, licenseTypeId }: { licenseIds: string[]; licenseTypeId: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await licensesAPI.bulkSetType(licenseIds, licenseTypeId);
			return response.data.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to set license type",
				);
			}
			return rejectWithValue("Failed to set license type");
		}
	},
);

// The stats endpoint's overview and status distribution as the card counts
function toLicenseStats(payload: unknown): LicenseStats | null {
	const response = payload as {
		data?: {
			overview?: { total: number; active: number; expired: number };
			statusDistribution?: Array<{ _id: string; count: number }>;
		};
	};
	const overview = response.data?.overview;
	if (!overview) return null;
	const statusDist = response.data?.statusDistribution || [];
	const getCount = (status: string) =>
		statusDist.find((s) => s._id === status)?.count || 0;
	return {
		total: overview.total,
		active: getCount("active"),
		inactive: getCount("inactive") + getCount("initialized"),
		expired: overview.expired,
		held: getCount("hold"),
	};
}

const licensesSlice = createSlice({
	name: "licenses",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearFilteredStats: (state) => {
			state.filteredStats = null;
			state.latestFilteredStatsRequestId = null;
		},
		setPage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		setLimit: (state, action: PayloadAction<number>) => {
			state.limit = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// No other case existed, so a failed stats call said nothing and
			// the cards kept their zeros or stale numbers
			.addCase(fetchLicenseStats.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Fetch license stats
			.addCase(fetchLicenseStats.fulfilled, (state, action) => {
				state.stats = toLicenseStats(action.payload) ?? state.stats;
			})
			// Filters change as fast as the user types; an older response must
			// not replace the counts for what is on screen now
			.addCase(fetchFilteredLicenseStats.pending, (state, action) => {
				state.latestFilteredStatsRequestId = action.meta.requestId;
			})
			.addCase(fetchFilteredLicenseStats.fulfilled, (state, action) => {
				if (state.latestFilteredStatsRequestId !== action.meta.requestId) return;
				state.filteredStats = toLicenseStats(action.payload);
			})
			.addCase(fetchFilteredLicenseStats.rejected, (state, action) => {
				if (state.latestFilteredStatsRequestId !== action.meta.requestId) return;
				state.filteredStats = null;
				state.error = action.payload as string;
			})
			// Fetch licenses
			.addCase(fetchLicenses.pending, (state, action) => {
				state.loading = true;
				state.error = null;
				state.listError = null;
				state.latestFetchRequestId = action.meta.requestId;
			})
			.addCase(fetchLicenses.fulfilled, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				state.listError = null;
				const response = action.payload as unknown as {
					data: { licenses: License[]; total: number };
				};
				state.licenses = response.data?.licenses || [];
				state.total = response.data?.total || 0;
			})
			.addCase(fetchLicenses.rejected, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				state.error = action.payload as string;
				state.listError =
					(action.payload as string) || "Failed to load licenses";
				// Keeping the previous rows would present them as the page that was
				// asked for. `total` stays, so the pagination the user needs to get
				// back off a failing page is still there.
				state.licenses = [];
			})
			// Create license
			.addCase(createLicense.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			// Deliberately does not splice the new license in. It was prepended
			// regardless of the active sort, filter and page, so it showed up in a
			// position it does not hold - and the refetch that every caller runs
			// straight afterwards is the real source of truth.
			.addCase(createLicense.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(createLicense.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Update license
			.addCase(updateLicense.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateLicense.fulfilled, (state, action) => {
				state.loading = false;
				const response = action.payload as unknown as {
					data: { license: License };
				};
				if (response.data?.license) {
					const index = state.licenses.findIndex(
						(license) => license._id === response.data.license._id,
					);
					if (index !== -1) {
						state.licenses[index] = response.data.license;
					}
				}
			})
			.addCase(updateLicense.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Delete license
			.addCase(deleteLicense.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteLicense.fulfilled, (state, action) => {
				state.loading = false;
				state.licenses = state.licenses.filter(
					(license) => license._id !== action.payload,
				);
				state.total -= 1;
			})
			.addCase(deleteLicense.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Hold license
			.addCase(holdLicense.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(holdLicense.fulfilled, (state, action) => {
				state.loading = false;
				const response = action.payload as unknown as {
					data: { license: License };
				};
				if (response.data?.license) {
					const index = state.licenses.findIndex(
						(license) => license._id === response.data.license._id,
					);
					if (index !== -1) {
						state.licenses[index] = response.data.license;
					}
				}
			})
			.addCase(holdLicense.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Bulk revoke licenses
			.addCase(bulkRevokeLicenses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(bulkRevokeLicenses.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(bulkRevokeLicenses.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Bulk hold licenses
			.addCase(bulkHoldLicenses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(bulkHoldLicenses.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(bulkHoldLicenses.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Bulk release licenses
			.addCase(bulkReleaseLicenses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(bulkReleaseLicenses.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(bulkReleaseLicenses.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Bulk renew licenses
			.addCase(bulkRenewLicenses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(bulkRenewLicenses.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(bulkRenewLicenses.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Bulk set license type
			.addCase(bulkSetLicenseType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(bulkSetLicenseType.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(bulkSetLicenseType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Bulk delete licenses
			.addCase(bulkDeleteLicenses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(bulkDeleteLicenses.fulfilled, (state, action) => {
				state.loading = false;
				const { licenseIds } = action.payload;
				state.licenses = state.licenses.filter(
					(license) => !licenseIds.includes(license._id),
				);
				state.total -= licenseIds.length;
			})
			.addCase(bulkDeleteLicenses.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const { clearError, clearFilteredStats, setPage, setLimit } = licensesSlice.actions;
export default licensesSlice.reducer;
